import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../shared/material/material.module';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ResourceOffering } from '../../../core/generated/model/resourceOffering';
import { ResourceOfferingControllerService } from '../../../core/generated/api/resourceOfferingController.service';
import { Address } from '../../../core/generated/model/address';
import { firstValueFrom } from 'rxjs';
import { AvailabilityComponent } from '../../../shared/availability/availability.component';
import { ProfileProviderPjControllerService } from '../../../core/generated/api/profileProviderPjController.service';
import { ProviderPJContextService } from '../../../shared/services/provider-pj-context.service';
import { ProfileProviderPJ } from '../../../core/generated';
import { ResourceExamesEnum, ResourceTerapiaEnum } from '../../../shared/enums';
import { getEnumKey } from '../../../shared/utils/enum.util';
import { ContainerComponent } from '../../../shared/container/container.component';

interface ResourceOption {
  label: string;
  key: string;
}

@Component({
    selector: 'app-resource-offering-provider-pj-form',
    standalone: true,
    imports: [CommonModule, MaterialModule, AvailabilityComponent, ContainerComponent],
    templateUrl: './resource-offering-provider-pj-form.component.html',
    styleUrls: ['./resource-offering-provider-pj-form.component.scss']
})
export class ResourceOfferingProviderPJFormComponent
  implements OnInit, OnDestroy {
  resourceOfferingProviderPJService = inject(ResourceOfferingControllerService);
  router: Router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  providerPJContextService = inject(ProviderPJContextService);
  providerPJId: string | null = null;
  profileProviderPJService = inject(ProfileProviderPjControllerService);
  providerPJ!: ProfileProviderPJ;
  id: string | null = null;
  addresses: Address[] = [];
  isMobile: boolean = false;
  subscriptions: any[] = [];

  // Lista unificada ordenada: exames (a-z) + terapias (a-z) — define a ordem dos controles do FormArray
  allResources: ResourceOption[] = [];

  examResources: ResourceOption[] = [];
  therapyResources: ResourceOption[] = [];

  formResourceOffering = this.fb.group({
    id: [''],
    cnpj: [''],
    resource: this.fb.array([]),
    address: [null as Address | null],
    publicationDate: [''],
    expirationPublicationDate: [''],
    availability: this.fb.array([]),
    description: [''],
  });

  constructor(
    private fb: FormBuilder,
    private deviceService: DeviceDetectorService
  ) {
  }

  ngOnInit() {
    this.isMobile = this.deviceService.isMobile();

    this.initializeResourceOptions();

    this.providerPJId =
      this.route.parent?.snapshot.paramMap.get('id') ??
      this.route.snapshot.paramMap.get('id') ??
      null;

    if (this.providerPJ) {
      this.formResourceOffering.get('cnpj')?.setValue(this.providerPJ.cnpj || null);
    }

    const subId = this.providerPJContextService.providerId$.subscribe((id) => {
      if (id !== null) this.providerPJId = id;
    });
    const subProfilePj = this.providerPJContextService.providerPJ$.subscribe(
      (c) => {
        if (c !== null && c !== this.providerPJ) {
          this.providerPJ = c;
          this.formResourceOffering.get('cnpj')?.setValue(c.cnpj || null);
        }
      }
    );
    this.subscriptions.push(subId, subProfilePj);

    const routeSnapshot = this.route.snapshot;
    if (routeSnapshot.paramMap.has('id')) {
      const idParam = routeSnapshot.paramMap.get('id');
      this.id = idParam ? idParam : null;
      if (this.id !== null) {
        this.loadResourceOffering(this.id);
      }
    }

    if (this.providerPJId) {
      this.loadAddresses(this.providerPJId).then();
    }

    this.formResourceOffering.setControl(
      'resource',
      this.fb.array(this.createResourceControls())
    );

    const days = [21, 22, 23, 31, 32, 33, 41, 42, 43, 51, 52, 53, 61, 62, 63, 71, 72, 73, 81, 82, 83];

    const availabilityArray = this.formResourceOffering.get(
      'availability'
    ) as FormArray;
    availabilityArray.clear();

    days.forEach((availability) => {
      availabilityArray.push(this.fb.control(availability));
    });
  }

  private initializeResourceOptions() {
    this.examResources = Object.keys(ResourceExamesEnum)
      .map((key) => ({
        key,
        label: ResourceExamesEnum[key as keyof typeof ResourceExamesEnum]
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    this.therapyResources = Object.keys(ResourceTerapiaEnum)
      .map((key) => ({
        key,
        label: ResourceTerapiaEnum[key as keyof typeof ResourceTerapiaEnum]
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Ordem canônica do FormArray: exames primeiro, depois terapias
    this.allResources = [...this.examResources, ...this.therapyResources];
  }

  // Retorna o índice do controle no FormArray pela chave do enum local
  getResourceIndex(resourceKey: string): number {
    return this.allResources.findIndex(r => r.key === resourceKey);
  }

  async loadAddresses(profileId: string): Promise<void> {
    const profileProviderId = parseInt(profileId, 10);
    return new Promise((resolve) => {
      const sub = this.profileProviderPJService
        .getAllAddresses(profileProviderId)
        .subscribe((addresses) => {
          this.addresses = addresses;
          if (!this.id && this.addresses.length > 0) {
            this.formResourceOffering
              .get('address')
              ?.setValue(this.addresses[0]);
          }
          resolve();
        });
      this.subscriptions.push(sub);
    });
  }

  async loadResourceOffering(id: string) {
    const resourceOffering: ResourceOffering = await firstValueFrom(
      this.resourceOfferingProviderPJService.findById4(Number(id))
    );

    const { address, id: resourceId, ...resourceOfferingWithoutAddress } = resourceOffering;
    
    this.formResourceOffering.patchValue({
      ...resourceOfferingWithoutAddress,
      id: resourceId?.toString() || ''
    });

    const availabilityArray = this.formResourceOffering.get('availability') as FormArray;
    availabilityArray.clear();
    if (resourceOffering.availability) {
      resourceOffering.availability.forEach((availability) => {
        availabilityArray.push(this.fb.control(availability));
      });
    }

    const incomingResources: string[] = resourceOffering.resource?.map(r => r.toString()) ?? [];
    this.setResourceControlsFromEnumArray(incomingResources);

    if (resourceOffering.address) {
      this.setAddressInForm(resourceOffering.address);
    }

    if (resourceOffering.cnpj) {
      this.formResourceOffering.get('cnpj')?.setValue(resourceOffering.cnpj);
    }
  }

  private setAddressInForm(resourceAddress: Address): void {
    const matchedAddress = this.addresses.find(
      (addr) => addr.id === resourceAddress.id
    );
    if (matchedAddress) {
      this.formResourceOffering.get('address')?.setValue(matchedAddress);
    } else {
      this.addresses.push(resourceAddress);
      this.formResourceOffering.get('address')?.setValue(resourceAddress);
    }
  }

  prepareResourceArray(): string[] {
    const arr = this.formResourceOffering.get('resource') as FormArray;
    if (!arr) return [];

    return this.allResources
      .filter((_, idx) => arr.at(idx)?.value === true)
      .map(r => r.key);
  }

  addResourceOffering() {
    const value = this.formResourceOffering.value;
    const availabilityArray = this.formResourceOffering.get('availability') as FormArray;

    let finalAvailability: number[] = [];
    const availabilityValues1 = availabilityArray.value as number[];
    const availabilityValues2 = availabilityArray.controls.map(c => c.value) as number[];
    const availabilityValues3 = value.availability as number[];

    if (availabilityValues1 && availabilityValues1.length > 0) {
      finalAvailability = availabilityValues1;
    } else if (availabilityValues2 && availabilityValues2.length > 0) {
      finalAvailability = availabilityValues2;
    } else if (availabilityValues3 && availabilityValues3.length > 0) {
      finalAvailability = availabilityValues3;
    }

    const statusValue = (!value.publicationDate || value.publicationDate === '')
      ? ResourceOffering.StatusEnum.Draft
      : ResourceOffering.StatusEnum.Open;

    const resourceOfferingProviderPJ: ResourceOffering = {
      id: value.id ? Number(value.id) : undefined,
      cnpj: this.providerPJ.cnpj,
      resource: this.prepareResourceArray() as any,
      address: value.address ? value.address : undefined,
      availability: finalAvailability,
      publicationDate: value.publicationDate ?? '',
      expirationPublicationDate: value.expirationPublicationDate ?? '',
      description: value.description ?? '',
      status: statusValue,
    };

    let sub;
    if (this.id !== null) {
      resourceOfferingProviderPJ.id = Number(this.id);
      sub = this.resourceOfferingProviderPJService
        .update3(Number(this.id), resourceOfferingProviderPJ)
        .subscribe({
          next: () => {
            this.router.navigate(['/providerPJ', this.providerPJId, 'resourceOffering']);
          }
        });
    } else {
      sub = this.resourceOfferingProviderPJService
        .save3(resourceOfferingProviderPJ)
        .subscribe({
          next: () => {
            this.router.navigate(['/providerPJ', this.providerPJId, 'resourceOffering']);
          }
        });
    }
    this.subscriptions.push(sub);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(
      (sub) => sub && sub.unsubscribe && sub.unsubscribe()
    );
  }

  dateFilter = (date: Date | null): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date ? date >= today : false;
  };

  dateLimitFilter = (date: Date | null): boolean => {
    let publishDate = new Date();
    let today = new Date();

    if (this.formResourceOffering.controls['publicationDate'].value) {
      publishDate = new Date(
        this.formResourceOffering.controls['publicationDate'].value
      );
    } else {
      this.formResourceOffering.controls['expirationPublicationDate'].reset();
    }

    if (this.formResourceOffering.controls['publicationDate'].value) {
      today = new Date(
        this.formResourceOffering.controls['publicationDate'].value
      );
    }
    today.setHours(0, 0, 0, 0);
    return date ? date >= today : false;
  };

  get availability() {
    return this.formResourceOffering.get('availability') as FormArray;
  }

  onAvailabilityChange(newAvailability: FormArray) {
    this.formResourceOffering.setControl('availability', newAvailability);
  }

  getAvailabilityValues(): number[] {
    const availabilityArray = this.formResourceOffering.get('availability') as FormArray;
    return availabilityArray ? availabilityArray.value : [];
  }

  private createResourceControls(): FormControl[] {
    return Array.from({ length: this.allResources.length }, () =>
      this.fb.control(false)
    );
  }

  get resourceArray(): FormArray {
    return this.formResourceOffering.get('resource') as FormArray;
  }

  private setResourceControlsFromEnumArray(incoming: string[]): void {
    const arr = this.formResourceOffering.get('resource') as FormArray;
    if (!arr) return;
    arr.controls.forEach((c) => c.setValue(false));

    incoming.forEach((item) => {
      const idx = this.allResources.findIndex(r => r.key === item);
      if (idx >= 0 && idx < arr.length) {
        arr.at(idx).setValue(true);
      }
    });
  }
}
